import {
  DEFAULT_TTL,
  VERIFICATION_TTL,
  getResendCooldownMs,
  getResendCooldownSeconds,
} from '../env/config';

// Storage keys
const PHONE_STORAGE_KEY = 'smartsend_verified_phone_by_order';
const CODE_STORAGE_KEY = 'smartsend_verification_code_by_order';
const ADDRESS_STORAGE_KEY = 'smartsend_address_by_order';
const ZONING_STORAGE_KEY = 'smartsend_zoning_data_by_order';
const RECIPIENT_INFO_STORAGE_KEY = 'smartsend_recipient_info_by_order';
const RESEND_COOLDOWN_KEY = 'smartsend_resend_cooldown_by_order';

// Import TTL constants from centralized config
// See: src/projects/SmartSend_v2/env/config.js for all configuration
const RESEND_COOLDOWN_TTL = getResendCooldownMs();
const SMARTSEND_KEY_PREFIX = 'smartsend_';

/**
 * Generic function to save a value to local storage
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 * @returns {boolean} Success status
 */
export const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error(`Failed to save ${key} to localStorage:`, error);
    return false;
  }
};

/**
 * Generic function to get a value from local storage
 * @param {string} key - Storage key
 * @returns {string|null} Stored value or null if not found
 */
export const getFromStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Failed to retrieve ${key} from localStorage:`, error);
    return null;
  }
};

/**
 * Generic function to remove a value from local storage
 * @param {string} key - Storage key
 * @returns {boolean} Success status
 */
export const clearFromStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Failed to clear ${key} from localStorage:`, error);
    return false;
  }
};

/**
 * Generic function to check if a value exists in storage
 * @param {string} key - Storage key
 * @returns {boolean}
 */
export const hasStoredValue = (key) => {
  return !!getFromStorage(key);
};

// ============================================
// TTL (TIME TO LIVE) SUPPORT
// ============================================

/**
 * Save a value with an expiration timestamp
 * @param {string} key - Storage key
 * @param {any} value - Value to store (will be JSON stringified)
 * @param {number} ttl - Time to live in milliseconds (default: 24 hours)
 * @returns {boolean} Success status
 */
export const saveWithTTL = (key, value, ttl = DEFAULT_TTL) => {
  try {
    const item = {
      value,
      expiry: Date.now() + ttl,
    };
    localStorage.setItem(key, JSON.stringify(item));
    return true;
  } catch (error) {
    console.error(`Failed to save ${key} with TTL:`, error);
    return false;
  }
};

/**
 * Get a value with TTL checking (auto-removes if expired)
 * @param {string} key - Storage key
 * @returns {any|null} Stored value or null if not found/expired
 */
export const getWithTTL = (key) => {
  try {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) {
      return null;
    }

    const item = JSON.parse(itemStr);

    // Check if item has expiry (TTL-enabled item)
    if (item.expiry && Date.now() > item.expiry) {
      // Item expired, remove it
      localStorage.removeItem(key);
      return null;
    }

    // Return the value (unwrap if TTL-enabled, otherwise return as-is)
    return item.value !== undefined ? item.value : item;
  } catch (error) {
    console.error(`Failed to retrieve ${key} with TTL:`, error);
    return null;
  }
};

/**
 * Clean up all expired SmartSend items from localStorage
 * Should be called on app mount
 * @returns {number} Number of items removed
 */
export const cleanupExpiredItems = () => {
  let removedCount = 0;

  try {
    const keys = Object.keys(localStorage);

    keys.forEach((key) => {
      // Only check SmartSend keys
      if (key.startsWith(SMARTSEND_KEY_PREFIX)) {
        try {
          const itemStr = localStorage.getItem(key);
          if (!itemStr) return;

          const item = JSON.parse(itemStr);

          // Legacy single-entry { value, expiry }
          if (item && typeof item === 'object' && 'expiry' in item && 'value' in item) {
            if (Date.now() > item.expiry) {
              localStorage.removeItem(key);
              removedCount++;
            }
            return;
          }

          // Order-scoped map { [orderId]: { value, expiry? } }
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            let updated = false;

            Object.keys(item).forEach((orderKey) => {
              const entry = item[orderKey];
              if (
                entry &&
                typeof entry === 'object' &&
                'expiry' in entry &&
                entry.expiry &&
                Date.now() > entry.expiry
              ) {
                delete item[orderKey];
                updated = true;
                removedCount++;
              }
            });

            if (updated) {
              if (Object.keys(item).length === 0) {
                localStorage.removeItem(key);
              } else {
                localStorage.setItem(key, JSON.stringify(item));
              }
            }
          }
        } catch (error) {
          // If parsing fails, it's not a TTL item, skip it
        }
      }
    });

    return removedCount;
  } catch (error) {
    console.error('Failed to cleanup expired items:', error);
    return removedCount;
  }
};

// ============================================
// ORDER-SCOPED STORAGE HELPERS
// ============================================

const loadOrderScopedData = (key) => {
  const raw = getFromStorage(key);
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    // Legacy format { value, expiry }
    if ('value' in parsed && 'expiry' in parsed) {
      clearFromStorage(key);
      return {};
    }

    return parsed;
  } catch (error) {
    console.error(`Failed to parse order-scoped data for ${key}:`, error);
    clearFromStorage(key);
    return {};
  }
};

const saveOrderScopedData = (key, map) => {
  if (!map || typeof map !== 'object') {
    return false;
  }
  if (Object.keys(map).length === 0) {
    return clearFromStorage(key);
  }
  return saveToStorage(key, JSON.stringify(map));
};

const saveOrderScopedWithTTL = (key, orderId, value, ttl = DEFAULT_TTL) => {
  if (!orderId) {
    return false;
  }
  const map = loadOrderScopedData(key);
  map[orderId] = {
    value,
    expiry: Date.now() + ttl,
  };
  return saveOrderScopedData(key, map);
};

const getOrderScopedValueWithTTL = (key, orderId) => {
  if (!orderId) {
    return null;
  }

  const map = loadOrderScopedData(key);
  const entry = map[orderId];

  if (!entry) {
    return null;
  }

  if (entry.expiry && Date.now() > entry.expiry) {
    delete map[orderId];
    saveOrderScopedData(key, map);
    return null;
  }

  return entry.value;
};

const clearOrderScopedValueWithTTL = (key, orderId) => {
  if (!orderId) {
    return clearFromStorage(key);
  }

  const map = loadOrderScopedData(key);
  if (map[orderId]) {
    delete map[orderId];
    return saveOrderScopedData(key, map);
  }
  return true;
};

const saveOrderScopedValue = (key, orderId, value) => {
  if (!orderId) {
    return false;
  }
  const map = loadOrderScopedData(key);
  map[orderId] = value;
  return saveOrderScopedData(key, map);
};

const getOrderScopedValue = (key, orderId) => {
  if (!orderId) {
    return null;
  }
  const map = loadOrderScopedData(key);
  return map[orderId] ?? null;
};

const clearOrderScopedValue = (key, orderId) => {
  if (!orderId) {
    return clearFromStorage(key);
  }
  const map = loadOrderScopedData(key);
  if (map[orderId]) {
    delete map[orderId];
    return saveOrderScopedData(key, map);
  }
  return true;
};

// ============================================
// PHONE NUMBER - Specific functions for convenience
// ============================================

/**
 * Save phone number to local storage with TTL and verification status
 * 
 * SECURITY NOTE: Phone number is saved with a 15-minute expiration.
 * This prevents UI bypass attacks where users manually set localStorage values.
 * Even if bypassed, backend still validates the verification code on submission.
 * 
 * @param {string} orderId - SmartSend order identifier used to scope storage
 * @param {string} phoneNumber - Phone number in E.164 format (e.g., "+14155552671")
 * @param {boolean} isPhoneVerified - Whether the phone number has been verified (default: false)
 * @returns {boolean} Success status
 */
export const savePhoneNumber = (orderId, phoneNumber, isPhoneVerified = false) => {
  const phoneData = {
    phoneNumber,
    isPhoneVerified,
  };
  return saveOrderScopedWithTTL(PHONE_STORAGE_KEY, orderId, phoneData, VERIFICATION_TTL);
};

/**
 * Get phone number from local storage (with TTL validation)
 * Automatically removes expired entries
 * 
 * Backward compatibility: Returns string for legacy entries, extracts phoneNumber from new format
 * 
 * @param {string} orderId - SmartSend order identifier used to scope storage
 * @returns {string|null} Phone number or null if not found/expired
 */
export const getPhoneNumber = (orderId) => {
  const phoneData = getOrderScopedValueWithTTL(PHONE_STORAGE_KEY, orderId);
  if (!phoneData) return null;
  
  // Handle legacy format (string) for backward compatibility
  if (typeof phoneData === 'string') {
    return phoneData;
  }
  
  // New format (object with phoneNumber and isPhoneVerified)
  return phoneData.phoneNumber || null;
};

/**
 * Get phone number with verification status from local storage
 * 
 * Backward compatibility: Legacy entries (strings) are assumed verified
 * 
 * @param {string} orderId - SmartSend order identifier used to scope storage
 * @returns {{phoneNumber: string, isPhoneVerified: boolean}|null} Phone data or null if not found/expired
 */
export const getPhoneNumberWithStatus = (orderId) => {
  const phoneData = getOrderScopedValueWithTTL(PHONE_STORAGE_KEY, orderId);
  if (!phoneData) return null;
  
  // Handle legacy format (string) for backward compatibility
  if (typeof phoneData === 'string') {
    return {
      phoneNumber: phoneData,
      isPhoneVerified: true, // Legacy entries are assumed verified
    };
  }
  
  // New format (object with phoneNumber and isPhoneVerified)
  return phoneData;
};

/**
 * Clear stored phone number for an order
 * 
 * @param {string} orderId - SmartSend order identifier
 * @returns {boolean} Success status
 */
export const clearPhoneNumber = (orderId) => {
  return clearOrderScopedValueWithTTL(PHONE_STORAGE_KEY, orderId);
};


// ============================================
// VERIFICATION CODE - Specific functions for convenience
// ============================================

/**
 * Save verification code to local storage with TTL
 * 
 * SECURITY NOTE: Verification code expires after 15 minutes.
 * This limits the window for potential UI bypass attacks and ensures
 * codes don't persist indefinitely. Backend validation is still the
 * primary security layer.
 * 
 * @param {string} code - Verification code from URL or SMS
 * @param {string} orderId - SmartSend order identifier used to scope storage
 * @returns {boolean} Success status
 */
export const saveVerificationCode = (orderId, code) => {
  return saveOrderScopedWithTTL(CODE_STORAGE_KEY, orderId, code, VERIFICATION_TTL);
};

/**
 * Get verification code from local storage (with TTL validation)
 * Automatically removes expired entries
 * 
 * @param {string} orderId - SmartSend order identifier used to scope storage
 * @returns {string|null} Verification code or null if not found/expired
 */
export const getVerificationCode = (orderId) => {
  return getOrderScopedValueWithTTL(CODE_STORAGE_KEY, orderId);
};


// ============================================
// ADDRESS - Specific functions for convenience
// ============================================

/**
 * Save address to local storage scoped by order
 * @param {string} orderId
 * @param {Object} address - Address object with all fields
 * @returns {boolean} Success status
 */
export const saveAddress = (orderId, address) => {
  try {
    if (!address) {
      return clearOrderScopedValue(ADDRESS_STORAGE_KEY, orderId);
    }
    return saveOrderScopedValue(ADDRESS_STORAGE_KEY, orderId, address);
  } catch (error) {
    console.error('Failed to save address:', error);
    return false;
  }
};

/**
 * @param {string} orderId
 * @returns {Object|null} Address object or null if not found
 */
export const getAddress = (orderId) => {
  try {
    const address = getOrderScopedValue(ADDRESS_STORAGE_KEY, orderId);
    return address || null;
  } catch (error) {
    console.error('Failed to retrieve address:', error);
    return null;
  }
};

/**
 * Remove address for specific order from local storage
 */
export const clearAddress = (orderId) => {
  return clearOrderScopedValue(ADDRESS_STORAGE_KEY, orderId);
};

// ============================================
// ZONING DATA - Specific functions for convenience
// ============================================

/**
 * @param {string} orderId
 * @param {Object} zoningData - Zoning validation response data
 * @returns {boolean} Success status
 */
export const saveZoningData = (orderId, zoningData) => {
  try {
    if (!zoningData) {
      return clearOrderScopedValue(ZONING_STORAGE_KEY, orderId);
    }
    return saveOrderScopedValue(ZONING_STORAGE_KEY, orderId, zoningData);
  } catch (error) {
    console.error('Failed to save zoning data:', error);
    return false;
  }
};

/**
 * @param {string} orderId
 * @returns {Object|null} Zoning data or null if not found
 */
export const getZoningData = (orderId) => {
  try {
    const data = getOrderScopedValue(ZONING_STORAGE_KEY, orderId);
    return data || null;
  } catch (error) {
    console.error('Failed to retrieve zoning data:', error);
    return null;
  }
};

/**
 * Remove zoning data for a specific order from local storage
 */
export const clearZoningData = (orderId) => {
  return clearOrderScopedValue(ZONING_STORAGE_KEY, orderId);
};

// ============================================
// RECIPIENT INFO MANAGEMENT
// ============================================

/**
 * Save recipient information to local storage (scoped by order)
 * @param {string} orderId
 * @param {Object} recipientInfo - Recipient information object
 * @returns {boolean} Success status
 */
export const saveRecipientInfo = (orderId, recipientInfo) => {
  try {
    if (!orderId || !recipientInfo || typeof recipientInfo !== 'object') {
      console.error('Invalid recipient info provided');
      return false;
    }
    return saveOrderScopedValue(RECIPIENT_INFO_STORAGE_KEY, orderId, recipientInfo);
  } catch (error) {
    console.error('Failed to save recipient info:', error);
    return false;
  }
};

/**
 * Get recipient information from local storage for specific order
 * @param {string} orderId
 * @returns {Object|null} Recipient info object or null if not found
 */
export const getRecipientInfo = (orderId) => {
  try {
    const info = getOrderScopedValue(RECIPIENT_INFO_STORAGE_KEY, orderId);
    return info || null;
  } catch (error) {
    console.error('Failed to retrieve recipient info:', error);
    return null;
  }
};

// ============================================
// RESEND CODE COOLDOWN - Specific functions for rate limiting
// ============================================

/**
 * Get the resend cooldown duration in seconds
 * Useful for displaying to users or setting initial timer state
 *
 * @returns {number} Cooldown duration in seconds
 */
export const getResendCooldownDuration = getResendCooldownSeconds;

/**
 * Save resend cooldown timestamp with environment-based TTL
 * Prevents users from requesting multiple codes within the cooldown period
 * Scoped by orderId so different orders can have independent cooldowns
 *
 * @param {string} orderId - SmartSend order identifier used to scope storage
 * @returns {boolean} Success status
 */
export const saveResendCooldown = (orderId) => {
  if (!orderId) {
    return false;
  }
  return saveOrderScopedWithTTL(RESEND_COOLDOWN_KEY, orderId, Date.now(), RESEND_COOLDOWN_TTL);
};

/**
 * Get remaining cooldown time in seconds
 * Returns null if cooldown has expired or doesn't exist
 * Scoped by orderId so different orders can have independent cooldowns
 *
 * @param {string} orderId - SmartSend order identifier used to scope storage
 * @returns {number|null} Remaining seconds or null if no cooldown
 */
export const getResendCooldownRemaining = (orderId) => {
  if (!orderId) {
    return null;
  }

  try {
    const map = loadOrderScopedData(RESEND_COOLDOWN_KEY);
    const entry = map[orderId];

    if (!entry) {
      return null;
    }

    // Check if item has expiry
    if (entry.expiry && Date.now() < entry.expiry) {
      // Calculate remaining time in seconds
      const remainingMs = entry.expiry - Date.now();
      return Math.ceil(remainingMs / 1000);
    }

    // Expired, cleanup
    delete map[orderId];
    saveOrderScopedData(RESEND_COOLDOWN_KEY, map);
    return null;
  } catch (error) {
    console.error('Failed to check resend cooldown:', error);
    return null;
  }
};

/**
 * Check if resend is currently allowed (no active cooldown)
 * Scoped by orderId so different orders can have independent cooldowns
 *
 * @param {string} orderId - SmartSend order identifier used to scope storage
 * @returns {boolean} True if resend is allowed
 */
export const canResendCode = (orderId) => {
  return getResendCooldownRemaining(orderId) === null;
};

/**
 * Clear resend cooldown (useful for testing or manual override)
 * Scoped by orderId so different orders can have independent cooldowns
 *
 * @param {string} orderId - SmartSend order identifier used to scope storage
 * @returns {boolean} Success status
 */
export const clearResendCooldown = (orderId) => {
  if (!orderId) {
    return false;
  }
  return clearOrderScopedValueWithTTL(RESEND_COOLDOWN_KEY, orderId);
};

// ============================================
// CLEAR ALL - For cleanup after submission
// ============================================

/**
 * Clear all SmartSend-related data from localStorage
 * Use this after successful form submission or when resetting the flow
 * @param {string} [orderId] - Optional SmartSend order identifier. If omitted, clears all orders.
 * @returns {boolean} Success status
 */
export const clearAllSmartSendData = (orderId) => {
  try {
    if (orderId) {
      clearOrderScopedValueWithTTL(PHONE_STORAGE_KEY, orderId);
      clearOrderScopedValueWithTTL(CODE_STORAGE_KEY, orderId);
      clearOrderScopedValue(ADDRESS_STORAGE_KEY, orderId);
      clearOrderScopedValue(ZONING_STORAGE_KEY, orderId);
      clearOrderScopedValue(RECIPIENT_INFO_STORAGE_KEY, orderId);
      clearResendCooldown(orderId);
    } else {
      clearFromStorage(PHONE_STORAGE_KEY);
      clearFromStorage(CODE_STORAGE_KEY);
      clearFromStorage(ADDRESS_STORAGE_KEY);
      clearFromStorage(ZONING_STORAGE_KEY);
      clearFromStorage(RECIPIENT_INFO_STORAGE_KEY);
      clearFromStorage(RESEND_COOLDOWN_KEY);
    }
    return true;
  } catch (error) {
    console.error('Failed to clear all SmartSend data:', error);
    return false;
  }
};
